/**
 * TriagePanel (13-25): the triage verb for all five registers through `VerbForm` (FIELDSET §3.3.1–§3.3.5, the triage
 * verb's own columns: Incidents 27 since 13.2, Complaints 12 since ISS-258, Feedback 8, Risk 11, CI 8).
 *
 * - The inputs are `verbInputColumns(register, 'triage')`: the verb's list less `Status` (the engine moves it New →
 *   Actioning on a first triage, D-13-20-2) and the stamps `TriagedAt` / `TriagedByUPN` (set when you save). The
 *   decider's reportability columns (`ReportabilityDecision`, the kinds, `NotificationDueAt`), the WHS answers and the
 *   notices are other verbs' (13-27 / 13-28) and are not in this list; the two allegation columns are in FIELDSET's
 *   triage list as well as the WHS verb's, so they stay (D-13-25-6).
 * - When the record names a client but has no client id (D-13-L-7; a FieldLink report, or a lodge marked "not on
 *   the client lists"), the panel leads with "Client id to add" and puts that field first.
 * - 13-33 (ISS-258, ISS-264): the client id is picked with 13-24's client picker in its id-only mode, searched from
 *   the name on the record, on Incidents and (since FIELDSET §3.3.2's amendment) Complaints. Only the id is written;
 *   the record's name stays. On a unit that is not a client stream the field stays a plain box.
 * - 13.2-09 (D-13.2-P-4, -P-5, -P-6), Incidents only:
 *   - **Complete the report first.** While the report lacks a field triage can't add (`columnsToComplete`, the same
 *     test as triage's `report-incomplete` gate and Today's "Complete the report"), a banner names the fields and links
 *     to `#panel-complete`, and the save is disabled. The fields stay editable.
 *   - The five 13.2 columns sit in two labelled sections after the existing fields, whose order is unchanged:
 *     **"Your view on reportability"** (`ReportabilityDraft`, `ReportabilityDraftNote`, `AwareAt`), with the authority
 *     line, and **"HR & WHS"** (`HrWhsTriageNeeded`, and `InjuredWorkerName` when a worker was injured).
 *   - The draft is the CM's view only. Nothing here writes `ReportabilityDecision`, a kind or `NotificationDueAt`:
 *     the engine refuses them on triage, and the decider makes the call (D-13.2-A-1).
 */

import { useCallback, useMemo, useState } from 'react';
import VerbForm from '../VerbForm';
import type { VerbControlProps } from '../VerbForm';
import ClientPicker from '../../lodge/ClientPicker';
import { CLIENT_STREAMS, fieldOf } from '../../../config/fieldset/registerFields';
import { SAH_DECIDER_ROLE } from '../../../config/fieldset/deciders';
import { liveChoice } from '../../../config/fieldset/live';
import { useActionRuntime } from '../../../hooks/useActionRuntime';
import { SAH_DELEGATES_LINE, needsClientId } from '../../../lib/recordAuthority';
import { isBlank } from '../../../lib/recordVerbs';
import { columnsToComplete } from '../../../lib/reportCompleteness';
import { verbInputColumns } from '../../../lib/verbForm';
import { searchClients } from '../../../services/clientDirectory';
import type { ClientSearchResult } from '../../../services/clientDirectory';
import type { RecordRow } from '../../../types/records';
import type { PanelProps } from './registry';
import styles from '../record.module.css';

/** The triage column that holds the client id (the CareManager lens's `client-id-to-add`, 13-22). */
const CLIENT_ID = 'RecipientClientId';

/** 13.2-09: the CM's draft view on reportability (D-13.2-P-5), in the plan's order. */
const DRAFT_SECTION: readonly string[] = ['ReportabilityDraft', 'ReportabilityDraftNote', 'AwareAt'];
/** 13.2-09: whether HR & WHS join triage (D-13.2-P-6), and who the injured worker is (D-13.2-P-2). */
const WHS_SECTION: readonly string[] = ['HrWhsTriageNeeded', 'InjuredWorkerName'];
const SECTIONED: ReadonlySet<string> = new Set([...DRAFT_SECTION, ...WHS_SECTION]);

const INJURED_WORKER_NAME = 'InjuredWorkerName';
const [WORKER_INJURED_YES] = liveChoice('incidents', 'WorkerInjured', 'Yes');

/** "(if they are away: the system owner, then the People & Culture lead)", or nothing when there are no delegates. */
const AWAY = SAH_DELEGATES_LINE ? ` (${SAH_DELEGATES_LINE.replace(/\.$/, '').replace(/^If/, 'if')})` : '';

/** The authority line (D-13.2-A-1's floor), fixed text, wherever the draft appears. */
const AUTHORITY_LINE =
  `This is your view, to help the decider. The reportability call is made by Oversight, or for aged care by ` +
  `${SAH_DECIDER_ROLE}${AWAY}. Nothing is sent to a regulator from here.`;

const NOTES: Readonly<Record<string, string>> = {
  AwareAt: 'When anyone at Harmony first knew about it. The decider may amend it.',
  InjuredWorkerName: 'Their name, as ShiftCare or Harmony AP has it, never an email address.',
};

type Search = (stream: string, query: string) => Promise<ClientSearchResult>;

/** The client id, picked from the client lists by the record's stream; only the id is set (ISS-258). */
function ClientIdControl({ control, stream, recordName, label, search }: { control: VerbControlProps; stream: string; recordName: string; label: string; search: Search }) {
  const [pickedName, setPickedName] = useState('');
  return (
    <ClientPicker
      idOnly
      stream={stream}
      search={search}
      label={label}
      required={false}
      name={pickedName}
      initialQuery={recordName}
      clientId={control.value}
      unlisted={false}
      onPick={(entry) => {
        setPickedName(entry?.name ?? '');
        control.set(entry?.id ?? '');
      }}
      onUnlistedChange={() => undefined}
      onUnlistedName={() => undefined}
      inputId={control.id}
    />
  );
}

/** The injured worker's name shows when the report says a worker was injured, or when the record already names one. */
function showsName(row: RecordRow): boolean {
  return row.fields.WorkerInjured === WORKER_INJURED_YES || !isBlank(row.fields[INJURED_WORKER_NAME]);
}

export default function TriagePanel({ row, siblings, actor, onSaved }: PanelProps) {
  const register = row.register;
  const lead = needsClientId(row);
  const { io } = useActionRuntime();
  const client = io?.client ?? null;
  const search = useCallback<Search>(
    (stream, query) => (client ? searchClients({ client }, stream, query) : Promise.reject(new Error('Not connected yet'))),
    [client],
  );
  const unit = typeof row.fields.BusinessUnit === 'string' ? row.fields.BusinessUnit : '';
  const recordName = typeof row.fields.RecipientName === 'string' ? row.fields.RecipientName : '';
  const { columns, firstDraft, firstWhs } = useMemo(() => {
    const own = verbInputColumns(register, 'triage');
    const rest = own.filter((c) => !SECTIONED.has(c));
    const ordered = lead && rest.includes(CLIENT_ID) ? [CLIENT_ID, ...rest.filter((c) => c !== CLIENT_ID)] : rest;
    const draft = DRAFT_SECTION.filter((c) => own.includes(c));
    const whs = WHS_SECTION.filter((c) => own.includes(c));
    return { columns: [...ordered, ...draft, ...whs], firstDraft: draft[0] ?? null, firstWhs: whs[0] ?? null };
  }, [register, lead]);

  // 13.2-09 (D-13.2-P-4): what only "Complete the report" can add. Empty off Incidents.
  const toComplete = useMemo(() => columnsToComplete(row), [row]);
  const blocked = toComplete.length > 0;

  return (
    <VerbForm
      register={register}
      verb="triage"
      row={row}
      siblings={siblings}
      actor={actor}
      columns={columns}
      saveLabel="Save triage"
      saveBlocked={blocked}
      onSaved={onSaved}
      showColumn={(column) => (column === INJURED_WORKER_NAME ? showsName(row) : true)}
      renderControl={(column, control) =>
        column === CLIENT_ID && CLIENT_STREAMS.includes(unit) ? (
          <ClientIdControl
            control={control}
            stream={unit}
            recordName={recordName}
            label={fieldOf(register, CLIENT_ID)?.label ?? CLIENT_ID}
            search={search}
          />
        ) : null
      }
      fieldNote={(column) => (NOTES[column] && SECTIONED.has(column) ? <p className={styles.hint}>{NOTES[column]}</p> : null)}
      beforeField={(column) => {
        if (column === firstDraft) {
          return (
            <div className={styles.groupHead}>
              <h4 className={styles.groupTitle}>Your view on reportability</h4>
              <p className={styles.authority}>{AUTHORITY_LINE}</p>
            </div>
          );
        }
        if (column === firstWhs) {
          return (
            <div className={styles.groupHead}>
              <h4 className={styles.groupTitle}>HR &amp; WHS</h4>
              <p className={styles.hint}>Whether HR &amp; WHS should join triage, and who the injured worker is when a worker was hurt.</p>
            </div>
          );
        }
        return null;
      }}
      lead={
        blocked || lead ? (
          <>
            {blocked && (
              <div className={styles.banner} role="status">
                <p>
                  <strong>Complete the report first:</strong> {toComplete.map((spec) => spec.label).join('; ')}
                </p>
                <p>Triage can be saved once the report has these.</p>
                <a href="#panel-complete">Go to Complete the report</a>
              </div>
            )}
            {lead && (
              <p className={styles.lead}>
                <strong>Client id to add.</strong> This record names a client but has no client id yet. Add their id from the
                client lists ({fieldOf(register, CLIENT_ID)?.label ?? CLIENT_ID}, first below).
              </p>
            )}
          </>
        ) : null
      }
    />
  );
}
